// STAFF-MFA-1 (30 Oct 2026, audit SCRM-06 / H6) — the second step of a staff
// sign-in, shown by LoginPage after a correct password.
//
//   next = "code"  → enter the 6-digit code from the authenticator app, or use
//                    Face ID / Touch ID, or a recovery code.
//   next = "enrol" → no second factor yet: scan a QR code (or type the key),
//                    enter a code, save the recovery codes, then continue.
//
// "Remember this device" skips this step here for 30 days.

import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, ScanFace, KeyRound, Copy, ArrowLeft } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { isPlatformPasskeyAvailable } from "@/lib/passkeys";

// SAF-37 — the same 52px pill buttons as the sign-in screen it sits on.
const primaryBtn =
  "w-full h-[52px] bg-navy hover:bg-navy-light text-white rounded-full text-base font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-50";
const secondaryBtn =
  "w-full h-[52px] border border-navy bg-white text-navy hover:bg-navy/5 rounded-full text-base font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-50";

function RememberDevice({ checked, onChange }) {
  return (
    <label className="flex items-start gap-2 text-sm text-gray-600 cursor-pointer">
      <input
        type="checkbox"
        className="mt-1"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        data-testid="mfa-remember"
      />
      <span>Remember this device for 30 days. Only tick this on your own computer or phone.</span>
    </label>
  );
}

function CodeInput({ value, onChange, inputRef, recovery }) {
  return (
    <Input
      ref={inputRef}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      inputMode={recovery ? "text" : "numeric"}
      autoComplete="one-time-code"
      placeholder={recovery ? "XXXXX-XXXXX" : "123 456"}
      maxLength={recovery ? 11 : 7}
      className="form-input text-center text-2xl tracking-[0.3em]"
      style={{ fontVariantNumeric: "lining-nums tabular-nums" }}
      data-testid="mfa-code"
    />
  );
}

export function RecoveryCodes({ codes, onDone, doneLabel = "I've saved them, continue" }) {
  const [saved, setSaved] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(codes.join("\n"));
      toast.success("Copied");
    } catch {
      toast.error("Couldn't copy. Write them down instead.");
    }
  };
  return (
    <div className="space-y-4" data-testid="mfa-recovery-codes">
      <p className="text-sm text-gray-600">
        These ten recovery codes each work once, if you ever lose your phone. Save them somewhere safe, such as your
        password manager. You won't see them again.
      </p>
      <ol className="grid grid-cols-2 gap-2 font-mono text-sm bg-gray-50 border border-gray-200 rounded p-3">
        {codes.map((c) => <li key={c}>{c}</li>)}
      </ol>
      <button type="button" onClick={copy} className={secondaryBtn}>
        <Copy className="w-4 h-4" /> Copy codes
      </button>
      <label className="flex items-start gap-2 text-sm text-gray-600 cursor-pointer">
        <input type="checkbox" className="mt-1" checked={saved} onChange={(e) => setSaved(e.target.checked)} />
        <span>I've saved my recovery codes</span>
      </label>
      <button type="button" disabled={!saved} onClick={onDone} className={primaryBtn} data-testid="mfa-recovery-done">
        {doneLabel}
      </button>
    </div>
  );
}

export function AuthenticatorSetup({ setup }) {
  return (
    <div className="space-y-3">
      <ol className="list-decimal pl-5 text-sm text-gray-600 space-y-1">
        <li>
          Install an authenticator app on your phone if you don't have one: Google Authenticator, Microsoft
          Authenticator, or the Passwords app on iPhone.
        </li>
        <li>In the app, add an account and scan this code.</li>
        <li>Type the 6-digit code the app shows.</li>
      </ol>
      {setup?.qr ? (
        <img src={setup.qr} alt="QR code for your authenticator app" className="mx-auto w-48 h-48 border border-gray-200 rounded" />
      ) : null}
      <div className="text-xs text-gray-500 text-center">
        Can't scan? Enter this key in the app instead (time-based):
        <div className="font-mono text-sm text-[#021d49] mt-1 select-all break-all" data-testid="mfa-manual-key">
          {setup?.secret}
        </div>
      </div>
    </div>
  );
}

export default function StaffSecondStep({ challenge, onSignedIn, onCancel }) {
  const { verifySecondFactor, startEnrolment, confirmEnrolment, finishEnrolment, loginWithPasskey } = useAuth();
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);
  const [useRecovery, setUseRecovery] = useState(false);
  const [setup, setSetup] = useState(null);
  const [pendingSession, setPendingSession] = useState(null);
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [passkeyHere, setPasskeyHere] = useState(false);
  const inputRef = useRef(null);
  const enrolling = challenge.next === "enrol";

  useEffect(() => {
    let alive = true;
    isPlatformPasskeyAvailable().then((ok) => { if (alive) setPasskeyHere(ok); });
    return () => { alive = false; };
  }, []);

  // Once per ticket: every call makes a new secret, so a re-render must not
  // start set-up again (startEnrolment isn't memoised in the context).
  const startedFor = useRef(null);
  useEffect(() => {
    if (!enrolling || startedFor.current === challenge.ticket) return;
    startedFor.current = challenge.ticket;
    startEnrolment(challenge.ticket)
      .then((d) => setSetup(d))
      .catch((e) => toast.error(e?.response?.data?.detail || "Couldn't start set-up. Sign in again."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrolling, challenge.ticket]);

  useEffect(() => { inputRef.current?.focus(); }, [useRecovery, setup]);

  const fail = (e) => {
    const status = e?.response?.status;
    toast.error(e?.response?.data?.detail || "That didn't work. Please try again.");
    if (status === 401) onCancel();
    setCode("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    try {
      if (enrolling) {
        const { data, recoveryCodes: codes } = await confirmEnrolment(challenge.ticket, code, remember);
        setPendingSession(data);
        setRecoveryCodes(codes);
      } else {
        const { user, recoveryCodesLeft } = await verifySecondFactor(challenge.ticket, code, remember);
        if (typeof recoveryCodesLeft === "number" && recoveryCodesLeft <= 3) {
          toast.warning(`You have ${recoveryCodesLeft} recovery codes left. Make new ones under Account security.`, { duration: 10000 });
        }
        onSignedIn(user);
      }
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const passkey = async () => {
    setBusy(true);
    try {
      const user = await loginWithPasskey(challenge.user?.email, { ticket: challenge.ticket, rememberDevice: remember });
      onSignedIn(user);
    } catch (err) {
      const name = err?.name || "";
      if (name !== "NotAllowedError" && name !== "AbortError") fail(err);
    } finally {
      setBusy(false);
    }
  };

  if (recoveryCodes) {
    return (
      <div>
        <h1 className="font-heading text-2xl font-bold text-[#021d49] mb-2">Save your recovery codes</h1>
        <RecoveryCodes codes={recoveryCodes} onDone={() => onSignedIn(finishEnrolment(pendingSession))} />
      </div>
    );
  }

  return (
    <div data-testid="mfa-step">
      <button type="button" onClick={onCancel} className="min-h-11 -ml-1 px-1 text-sm font-semibold text-gold-ink hover:text-navy flex items-center gap-1 mb-2">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <h1 className="font-heading text-2xl font-bold text-[#021d49] mb-2 flex items-center gap-2">
        <ShieldCheck className="w-6 h-6 text-[#bd9a59]" />
        {enrolling ? "Set up your sign-in code" : "Enter your code"}
      </h1>
      <p className="text-ink-muted mb-6 text-sm">
        {enrolling
          ? "Staff accounts now need a code from an authenticator app as well as a password. This takes about two minutes, once."
          : useRecovery
            ? "Enter one of the recovery codes you saved when you set up your authenticator."
            : "Open your authenticator app and enter the 6-digit code for Solara CRM."}
      </p>

      {enrolling && <AuthenticatorSetup setup={setup} />}

      <form onSubmit={submit} className="space-y-4 mt-4">
        <div>
          <Label className="form-label">{useRecovery ? "Recovery code" : "6-digit code"}</Label>
          <CodeInput value={code} onChange={setCode} inputRef={inputRef} recovery={useRecovery} />
        </div>
        <RememberDevice checked={remember} onChange={setRemember} />
        <button type="submit" disabled={busy || (enrolling && !setup)} className={primaryBtn} data-testid="mfa-submit">
          {busy ? (
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : enrolling ? "Turn on and continue" : "Sign in"}
        </button>
      </form>

      {!enrolling && (
        <div className="mt-4 space-y-3">
          {challenge.methods?.passkey && passkeyHere && (
            <button type="button" onClick={passkey} disabled={busy} className={secondaryBtn} data-testid="mfa-passkey">
              <ScanFace className="w-5 h-5" /> Use Face ID / Touch ID instead
            </button>
          )}
          {challenge.methods?.totp && (
            <button
              type="button"
              onClick={() => { setUseRecovery(!useRecovery); setCode(""); }}
              className="w-full text-sm text-gray-500 hover:text-[#bd9a59] flex items-center justify-center gap-1"
              data-testid="mfa-use-recovery"
            >
              <KeyRound className="w-4 h-4" />
              {useRecovery ? "Use a code from the app" : "Lost your phone? Use a recovery code"}
            </button>
          )}
          <p className="text-xs text-gray-400 text-center">
            No phone and no recovery codes? Ask Mark to reset your sign-in code.
          </p>
        </div>
      )}
    </div>
  );
}
